import React, { useState, useEffect, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import api from '../../utils/api';

const turnstileSiteKey = process.env.REACT_APP_CF_TURNSTILE_SITE_KEY || '';

const AuthPage = ({ onLogin, onRegister }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [turnstileRequired, setTurnstileRequired] = useState(() => (turnstileSiteKey ? null : false));
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (!turnstileSiteKey) {
      setTurnstileRequired(false);
      return undefined;
    }
    let cancelled = false;
    api
      .get('/api/auth/turnstile-required')
      .then((res) => {
        if (!cancelled) setTurnstileRequired(!!res.data?.required);
      })
      .catch(() => {
        if (!cancelled) setTurnstileRequired(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    let tokenToSend = turnstileToken;
    if (turnstileRequired) {
      // With execution=execute, the token is only available after the user click triggers execution.
      if (!tokenToSend) {
        try {
          const inst = turnstileRef.current;
          if (!inst) {
            throw new Error('Turnstile not ready');
          }
          inst.execute();
          tokenToSend = await inst.getResponsePromise(30000);
          setTurnstileToken(tokenToSend);
        } catch (err) {
          setError('Please complete the security check.');
          setTurnstileToken(null);
          turnstileRef.current?.reset();
          setLoading(false);
          return;
        }
      }
    }

    const turnstilePayload = tokenToSend ? { turnstileToken: tokenToSend } : {};

    try {
      if (isLogin) {
        const response = await api.post('/api/auth/login', { email, password, ...turnstilePayload });
        console.log('Login response:', response.data);
        localStorage.setItem('token', response.data.token); // Use localStorage for persistent login
        sessionStorage.removeItem('token'); // Clean up any old sessionStorage token
        if (onLogin) onLogin(response.data.user);
        
        // Check subscription and user-specific store connection
        try {
          const subRes = await api.get('/api/subscribe/status');
          const isSubscribed = subRes.data.status === 'active' || subRes.data.status === 'trialing' || (subRes.data.plan && subRes.data.plan !== 'free');
          
          if (isSubscribed) {
            // Check if user has a connected store
            try {
              const storeUrl = localStorage.getItem('storeUrl');
              const platform = localStorage.getItem('platform');
              const connectionRes = await api.get('/api/stores/check-connection', {
                params: {
                  ...(storeUrl && { store_url: storeUrl }),
                  ...(platform && { platform: platform })
                }
              });
              const hasConnection = connectionRes.data.hasConnection;
              
              if (hasConnection) {
                // User has a connected store, set localStorage and redirect to dashboard
                // Only update if not already set (to preserve user's selection)
                if (!localStorage.getItem('storeUrl')) {
                  localStorage.setItem('storeUrl', connectionRes.data.store_url);
                }
                if (!localStorage.getItem('platform')) {
                  localStorage.setItem('platform', connectionRes.data.platform);
                }
                localStorage.setItem('isConnected', 'true');
                localStorage.setItem('platform', connectionRes.data.platform);
                localStorage.setItem('storeUrl', connectionRes.data.store_url);
            window.location.href = '/#/dashboard';
            return;
              } else {
                // User is subscribed but no store connected
                localStorage.removeItem('isConnected');
                localStorage.removeItem('platform');
                localStorage.removeItem('storeUrl');
                window.location.href = '/#/store-setup';
                return;
              }
            } catch (connectionErr) {
              console.error('Error checking store connection:', connectionErr);
              // If we can't check connection, assume no connection
              localStorage.removeItem('isConnected');
              localStorage.removeItem('platform');
              localStorage.removeItem('storeUrl');
            window.location.href = '/#/store-setup';
            return;
            }
          } else {
            // User is not subscribed
            localStorage.removeItem('isConnected');
            localStorage.removeItem('platform');
            localStorage.removeItem('storeUrl');
            window.location.href = '/#/pricing';
            return;
          }
        } catch (e) {
          console.error('Error checking subscription:', e);
          localStorage.removeItem('isConnected');
          localStorage.removeItem('platform');
          localStorage.removeItem('storeUrl');
          window.location.href = '/#/pricing';
        }
      } else {
        // Registration - clear any existing store connection data
        localStorage.removeItem('isConnected');
        localStorage.removeItem('platform');
        localStorage.removeItem('storeUrl');
        localStorage.removeItem('store_connection');
        
        const response = await api.post('/api/auth/register', { name, email, password, ...turnstilePayload });
        localStorage.setItem('token', response.data.token);
        sessionStorage.removeItem('token');
        if (onRegister) onRegister(response.data.user);
        // After registration, go to pricing (HashRouter requires #)
        window.location.href = '/#/pricing';
      }
    } catch (err) {
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setError(
        err.response?.data?.error || 
        (isLogin ? 'Login failed. Please check your credentials.' : 'Registration failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      setSuccessMessage(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgotPassword(false);
    setError(null);
    setSuccessMessage(null);
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  const toggleForgotPassword = () => {
    setShowForgotPassword(!showForgotPassword);
    setError(null);
    setSuccessMessage(null);
    setPassword('');
    setConfirmPassword('');
  };

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleForgotPassword}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-green-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 010 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-800">{successMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={toggleForgotPassword}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Back to {isLogin ? 'Sign in' : 'Sign up'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Login/Register Form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {isLogin ? 'Sign in to your account' : 'Create your account'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={toggleMode}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            {isLogin ? 'Sign up here' : 'Sign in here'}
          </button>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {!isLogin && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required={!isLogin}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

          <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
            <input
                  id="email"
                  name="email"
              type="email"
                  autoComplete="email"
                  required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
            </div>

          <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
            <input
                  id="password"
                  name="password"
              type="password"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required={!isLogin}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
              </div>
            )}

            {isLogin && (
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={toggleForgotPassword}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {turnstileRequired && turnstileSiteKey && (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => {
                    setTurnstileToken(null);
                    turnstileRef.current?.reset();
                  }}
                  onError={() => setTurnstileToken(null)}
                options={{ execution: 'execute', appearance: 'execute' }}
                />
              </div>
            )}

            <div>
          <button
            type="submit"
            disabled={
              loading ||
              (turnstileSiteKey && turnstileRequired === null)
            }
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </div>
                ) : (
                  isLogin ? 'Sign in' : 'Create account'
                )}
          </button>
            </div>
        </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  Welcome to CartResQ
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage; 